import { type Market } from "@dotcom/markets";
import { isFullUrl } from "@dotcom/url";
import {
  AnchorButton,
  ButtonVariants,
  ConvertHTMLtoComponents,
  Heading,
  Icons,
  Quote,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import { type HTMLProps } from "react";

import { LocalizedLink } from "../..";

import {
  StyledColumn,
  StyledGrid,
  StyledInfoColumn,
  StyledMessage,
} from "./NewsTextSection.styles";

import { Section } from "~/common/components";
import { type ParsedWwwButton } from "~/common/queries/wwwButton";

interface Props {
  readonly text?: string;
  readonly info?: string;
  readonly quote?: string;
  readonly quotee?: string;
  readonly callToAction?: ParsedWwwButton;
  readonly sectionSlug?: string;
  readonly isChina: boolean;
  readonly market: Market;
}

const NewsTextSection = ({
  text,
  info,
  quote,
  quotee,
  callToAction,
  sectionSlug,
  isChina,
  market,
}: Props) => {
  return (
    <Section id={sectionSlug}>
      <StyledGrid>
        {info && (
          <StyledInfoColumn>
            <StyledMessage>{info}</StyledMessage>
          </StyledInfoColumn>
        )}

        {quote && (
          <StyledInfoColumn>
            <Quote author={quotee}>{quote}</Quote>
          </StyledInfoColumn>
        )}

        <StyledColumn>
          {text && (
            <ConvertHTMLtoComponents
              overrideElementMap={{
                a({ children, ...props }: HTMLProps<HTMLAnchorElement>) {
                  return (
                    <LocalizedLink
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                      href={props.href ?? ""}
                      target={props.target ?? ""}
                      rel={props.rel ?? ""}
                      isChina={isChina}
                    >
                      {children}
                    </LocalizedLink>
                  );
                },
                h1({ children }) {
                  return <Heading level={2}>{children}</Heading>;
                },
              }}
            >
              {text}
            </ConvertHTMLtoComponents>
          )}

          {callToAction && (
            <>
              <Spacer spacing={Spacings.small} />
              <AnchorButton
                href={
                  isFullUrl(callToAction.url ?? "")
                    ? (callToAction.url ?? "")
                    : `/${market.locale}${callToAction.url ?? ""}`
                }
                variant={ButtonVariants.text}
                icon={Icons.arrowRight}
                target={callToAction.target}
                rel={callToAction.target === "_blank" ? "noopener" : undefined}
              >
                {callToAction.label}
              </AnchorButton>
            </>
          )}
        </StyledColumn>
      </StyledGrid>
    </Section>
  );
};

export default NewsTextSection;
